import { ref, computed, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useCurrentUserStore } from "@stores/currentUser";
import { useSurveyCompletionStore } from "@stores/survey/surveyCompletion";
import {
  useFiltersUrlSync,
  arrayParam,
  numberParam,
  stringParam,
  type UrlFiltersState,
} from "@/utils/composables/filters/useFiltersUrlSync";
import type { FilterConfig, FilterValues, DateRangeValue, NumberRangeValue } from "@/components/episto-ui/filter-dropdown";

const AGE_MIN = 0;
const AGE_MAX = 100;

const urlParams = {
  from: stringParam(),
  to: stringParam(),
  countries: arrayParam(),
  targets: arrayParam(),
  masterCampaigns: arrayParam(),
  campaigns: arrayParam(),
  audiences: arrayParam(),
  genders: arrayParam(),
  ageMin: numberParam(AGE_MIN),
  ageMax: numberParam(AGE_MAX),
};

type CompletionUrlState = UrlFiltersState<typeof urlParams>;

export const useCompletionFilters = () => {
  const currentUserStore = useCurrentUserStore();
  const surveyCompletionStore = useSurveyCompletionStore();

  const { isAdmin } = storeToRefs(currentUserStore);
  const {
    filtersCountryOptions,
    filtersTargetOptions,
    filtersGenderOptions,
    filtersMasterCampaignsOptions,
    filtersCampaignsOptions,
    filtersAudiencesOptions,
    hasSurveyCampaigns,
  } = storeToRefs(surveyCompletionStore);

  const { fetchAdminFiltersData, applyCompletionFilters, resetCompletionFilters } = surveyCompletionStore;

  const filterValues = ref<FilterValues>({});
  const showAdminFilters = computed(() => isAdmin.value && hasSurveyCampaigns.value);
  const filterConfigs = computed<FilterConfig[]>(() => [
    {
      id: "dates",
      type: "dateRange",
      label: "Date",
    },
    {
      id: "countries",
      label: "Country",
      options: filtersCountryOptions.value || [],
    },
    {
      id: "targets",
      label: "Target",
      options: filtersTargetOptions.value || [],
    },
    {
      id: "masterCampaigns",
      label: "Master Campaign",
      hasSearch: true,
      options: filtersMasterCampaignsOptions.value || [],
      isVisible: showAdminFilters.value,
    },
    {
      id: "campaigns",
      label: "Campaign",
      hasSearch: true,
      options: filtersCampaignsOptions.value || [],
      isVisible: showAdminFilters.value,
    },
    {
      id: "audiences",
      label: "Audience",
      hasSearch: true,
      options: filtersAudiencesOptions.value || [],
      isVisible: showAdminFilters.value,
    },
    {
      id: "genders",
      label: "Gender (adset)",
      options: filtersGenderOptions.value || [],
      isVisible: showAdminFilters.value,
    },
    {
      id: "age",
      type: "numberRange",
      label: "Age (adset)",
      defaultMin: 0,
      defaultMax: 100,
      minLimit: 0,
      maxLimit: 100,
      isVisible: showAdminFilters.value,
    },
  ]);

  const syncFiltersToStore = () => {
    const store = surveyCompletionStore;
    const dates = filterValues.value.dates as DateRangeValue | undefined;
    const age = filterValues.value.age as NumberRangeValue | undefined;

    store.completionFilters.dates = dates?.start && dates?.end ? [dates.start, dates.end] : [];
    store.completionFilters.countries = (filterValues.value.countries as string[]) || [];
    store.completionFilters.targets = (filterValues.value.targets as string[]) || [];
    store.completionFilters.master_campaigns = (filterValues.value.masterCampaigns as string[]) || [];
    store.completionFilters.campaigns = (filterValues.value.campaigns as string[]) || [];
    store.completionFilters.audiences = (filterValues.value.audiences as string[]) || [];
    store.completionFilters.genders = (filterValues.value.genders as string[]) || [];
    store.completionFilters.age = age ? [age.min, age.max] : [0, 100];
  };

  const serializeUrlState = (): CompletionUrlState => {
    const dates = filterValues.value.dates as DateRangeValue | undefined;
    const age = filterValues.value.age as NumberRangeValue | undefined;

    return {
      from: dates?.start || "",
      to: dates?.end || "",
      countries: (filterValues.value.countries as string[]) || [],
      targets: (filterValues.value.targets as string[]) || [],
      masterCampaigns: (filterValues.value.masterCampaigns as string[]) || [],
      campaigns: (filterValues.value.campaigns as string[]) || [],
      audiences: (filterValues.value.audiences as string[]) || [],
      genders: (filterValues.value.genders as string[]) || [],
      ageMin: age?.min ?? AGE_MIN,
      ageMax: age?.max ?? AGE_MAX,
    };
  };

  // A filter the dropdown never set must stay absent rather than become an
  // empty array: the dropdown reads its own emptiness from the missing key.
  const applyUrlState = (state: CompletionUrlState) => {
    const values: FilterValues = {};
    if (state.from && state.to) values.dates = { start: state.from, end: state.to };
    if (state.countries.length) values.countries = state.countries;
    if (state.targets.length) values.targets = state.targets;
    if (state.masterCampaigns.length) values.masterCampaigns = state.masterCampaigns;
    if (state.campaigns.length) values.campaigns = state.campaigns;
    if (state.audiences.length) values.audiences = state.audiences;
    if (state.genders.length) values.genders = state.genders;
    if (state.ageMin !== AGE_MIN || state.ageMax !== AGE_MAX) {
      values.age = { min: state.ageMin, max: state.ageMax };
    }

    filterValues.value = values;
    syncFiltersToStore();
  };

  const { syncUrl, restoreFromUrl } = useFiltersUrlSync({
    params: urlParams,
    serialize: serializeUrlState,
    apply: applyUrlState,
    onExternalChange: async (state) => {
      applyUrlState(state);
      await applyCompletionFilters();
    },
  });

  // Synchronous, so the store already carries the filters when the view fetches
  // on mount: a shared URL loads its own slice in one request, not two.
  restoreFromUrl();

  const applyFilters = async () => {
    syncFiltersToStore();
    await applyCompletionFilters();
    syncUrl();
  };

  const clearFilters = async () => {
    filterValues.value = {};
    resetCompletionFilters();
    await applyCompletionFilters();
    syncUrl();
  };

  const init = async () => await fetchAdminFiltersData();

  onMounted(init);

  return {
    filterConfigs,
    filterValues,
    applyFilters,
    clearFilters,
  };
};
